import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import img3 from "../../images/_celebration.png";
import img1 from "../../images/_space.png";
import img2 from "../../images/animation-calendar.gif";

interface ModernHowItWorksSectionProps {
  className?: string;
}

const ModernHowItWorksSection: React.FC<ModernHowItWorksSectionProps> = ({ className = "" }) => {
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  const steps = [
    {
      id: "01",
      title: "Trouver votre espace",
      description:
        "Parcourez notre sélection variée d’espaces événementiels : lofts, villas, salles de réception, jardins,… Filtrez par type d’événement, capacité et localisation pour trouver l’espace qui vous correspond",
      image: img1,
      color: "bg-gradient-to-br from-[#4663ac] to-[#f7e67c]",
    },
    {
      id: "02",
      title: "Réserver",
      description:
        "Accédez aux fiches détaillées des lieux, avec photos, descriptions, tarifs et disponibilités. Réservez en toute sérénité et contactez directement les hôtes pour obtenir davantage d’informations.",
      image: img2,
      color: "bg-gradient-to-br from-[#c1d8f0] to-[#f79dcb]",
    },
    {
      id: "03",
      title: "Célébrer",
      description:
        "Le jour J, profitez pleinement de votre événement. Que ce soit pour un mariage, une conférence ou une soirée privée, nous vous aidons à réaliser votre projet.",
      image: img3,
      color: "bg-gradient-to-br from-[#f7e67c] to-[#4663ac]",
    },
  ];

  return (
    <div className={`py-20 bg-white dark:bg-gray-900 ${className}`} ref={ref}>
      <div className="container mx-auto px-4">
        {/* Section Header - Plus épuré */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Comment ça marche ?
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Réservez votre espace événementiel en 3 étapes simples
          </p>
        </motion.div>

        {/* Steps - Design épuré et moderne */}
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {steps.map((step, index) => {
              const isLastStep = index === steps.length - 1;

              return (
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  className="relative group text-center"
                >
                  {/* Connecting Line */}
                  {!isLastStep && (
                    <div className="hidden md:block absolute top-12 left-full w-12 h-0.5 bg-gradient-to-r from-gray-300 to-gray-200 dark:from-gray-600 dark:to-gray-700 z-0">
                      <motion.div
                        className="h-full bg-gradient-to-r from-[#4663ac] to-[#c1d8f0] origin-left"
                        initial={{ scaleX: 0 }}
                        animate={inView ? { scaleX: 1 } : {}}
                        transition={{ delay: index * 0.2 + 0.8, duration: 0.8 }}
                      />
                    </div>
                  )}

                  {/* Step Content */}
                  <div className="relative z-10">
                    {/* Icon Container - Plus minimaliste */}
                    <div className="mb-6">
                      <div
                        className={`w-32 h-32 bg-white dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto transition-all duration-300 group-hover:scale-105`}
                      >
                        <img src={step.image} alt={step.title} className="w-20 h-20 object-contain" />
                      </div>

                      {/* Step Number - Plus discret */}
                      <div className="mt-4">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full text-sm font-bold">
                          {step.id}
                        </span>
                      </div>
                    </div>

                    {/* Text Content - Plus épuré */}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{step.title}</h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{step.description}</p>
                    </div>
                  </div>

                  {/* Hover Effect - Subtil */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-transparent to-gray-50 dark:to-gray-800/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                </motion.div>
              );
            })}
          </div>

          {/* Call to Action - Plus discret */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1, duration: 0.8 }}
            className="text-center mt-16"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/espace-evenementiel")}
              className="inline-flex items-center space-x-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-8 py-3 rounded-xl font-semibold transition-all duration-300 hover:shadow-lg"
            >
              <span>Commencer maintenant</span>
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ModernHowItWorksSection;
